// Loads register assets into Cesium and manages visibility / opacity / wireframe.
//
// Placement: the backend provides a column-major 4x4 (East|North|Up|origin in ECEF) and
// authors GLB vertices as local ENU (east, north, up). Cesium maps model coords -> world
// via modelMatrix directly, so modelMatrix * vertex lands correctly with no axis remap.
import {
  Viewer,
  Model,
  Matrix4,
  Cartesian3,
  Color,
  Cartographic,
  Math as CMath,
  GeoJsonDataSource,
  ClassificationType,
  Entity,
  HeightReference,
  VerticalOrigin,
  ColorBlendMode,
  Axis,
  ImageryLayer,
  UrlTemplateImageryProvider,
  Rectangle,
  CesiumTerrainProvider,
  EllipsoidTerrainProvider,
  BoundingSphere,
  CustomShader,
  TextureUniform,
  UniformType,
  VaryingType,
  GroundPolylinePrimitive,
  GroundPolylineGeometry,
  GeometryInstance,
  PolylineMaterialAppearance,
  Material,
  CustomDataSource,
  CallbackProperty,
  TextureMinificationFilter,
  Intersect,
} from "cesium";

/**
 * Viewer-side approximation of Unity's Viewport/EastStudies/GroundContent_DumpsPits shader,
 * which is the signed-off look for the East Studies pits and waste dumps. The raw GLB
 * material (one base map) reads too light next to it. With _HasGrimeAO = 0 (every pit/dump
 * material) Unity's only active darkening is the coarse-dirt colour overlay:
 *   mask   = saturate(saturate((grime.r - 0.5) * 1.69 + 0.5) + 0.36)
 *            grime = ES_GrimeMask sampled triplanar in world space, 684.4 m per tile
 *   albedo = lerp(albedo, albedo * coarseDirt * 2, mask)
 *            coarseDirt = 1_CoarseDirt_BaseColor, 80 m per tile, triplanar
 * (plus a coarse-dirt normal overlay, not replicated). Here both maps tile in local-ENU
 * plan - the GLB verts are local-ENU metres, so positionMC.xy IS east/north - which is
 * what the triplanar reduces to on these mostly-flat surfaces. Roughness/metallic still
 * come from the GLB's own ORM. Applied to assets whose meta carries
 * shader="ground_dumps_pits" (set by east_studies_build.py).
 */
let groundShader: CustomShader | null = null;
function groundDumpsPitsShader(): CustomShader {
  if (groundShader) return groundShader;
  groundShader = new CustomShader({
    uniforms: {
      u_dirt: { type: UniformType.SAMPLER_2D, value: new TextureUniform({ url: "/es/coarse_dirt.jpg", repeat: true }) },
      u_grime: { type: UniformType.SAMPLER_2D, value: new TextureUniform({ url: "/es/grime_mask.jpg", repeat: true }) },
      u_dirtTile: { type: UniformType.FLOAT, value: 80.0 },
      u_grimeTile: { type: UniformType.FLOAT, value: 684.4 },
    },
    fragmentShaderText: `
      void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
        vec2 plan = fsInput.attributes.positionMC.xy;            // local ENU metres
        vec3 dirt = texture(u_dirt, fract(plan / u_dirtTile)).rgb;
        float g = texture(u_grime, fract(plan / u_grimeTile)).r;
        float mask = clamp(clamp((g - 0.5) * 1.69 + 0.5, 0.0, 1.0) + 0.36, 0.0, 1.0);
        material.diffuse = mix(material.diffuse, material.diffuse * dirt * 2.0, mask);
      }
    `,
  });
  return groundShader;
}

/**
 * Spinning wind-turbine rotors, on the GPU. An animated fleet GLB (backend acciona_fleets.py with
 * ACCIONA_TURBINE_ANIMATE=1) is still ONE joined mesh, but every vertex carries _ROTOR_HUB (rotor
 * centre), _ROTOR_AXIS (unit spin axis, pointing downwind) and _ROTOR_SPIN (rad/s, phase rad;
 * 0, 0 on the tower and nacelle), all in the model's own ECEF-local frame. The vertex stage turns
 * rotor vertices about their hub by phase + speed * u_time (Rodrigues); the fragment stage turns the
 * shading normal by the same angle, since CustomShader can't rewrite the normal attribute. Each
 * turbine has its own speed and phase, so no two turn in lockstep. Applied to assets whose meta
 * carries shader="turbine_rotor".
 *
 * The rotors are POSED, not spun: u_time stays 0, so each rotor sits at its own phase. The loop that
 * advanced u_time ~30x a second kept the whole scene rendering while a fleet was on screen (25 renders
 * a second with nobody touching it, measured 7 Oct 2026), which is what loaded the GPU. Chad, 7 Oct:
 * "if animation kills it, remove it".
 *
 * Blade markings ("Red tips", the Unity app's toggle): a fleet whose meta carries `markings` gets its
 * OWN shader with the marked rotor BaseMap as a texture uniform. It shares the GLB rotor's UVs, so
 * with u_markings on, rotor fragments (spin speed != 0) read it instead of the plain map. The GLB
 * samples its own texture unflipped while a TextureUniform loads with flipY, hence 1 - v.
 */
let rotorShader: CustomShader | null = null;
function turbineRotorShader(markedUrl?: string): CustomShader {
  if (!markedUrl && rotorShader) return rotorShader;
  const marked = !!markedUrl;
  const shader = new CustomShader({
    uniforms: {
      u_time: { type: UniformType.FLOAT, value: 0.0 },
      ...(marked
        ? {
            u_markings: { type: UniformType.FLOAT, value: 0.0 },
            u_marked: {
              type: UniformType.SAMPLER_2D,
              value: new TextureUniform({
                url: markedUrl,
                repeat: false,
                minificationFilter: TextureMinificationFilter.LINEAR_MIPMAP_LINEAR,
              }),
            },
          }
        : {}),
    },
    varyings: { v_rotorAxis: VaryingType.VEC3, v_rotorAngle: VaryingType.FLOAT, v_rotor: VaryingType.FLOAT },
    vertexShaderText: `
      void vertexMain(VertexInput vsInput, inout czm_modelVertexOutput vsOutput) {
        vec2 spin = vsInput.attributes.rotor_spin;
        vec3 k = vsInput.attributes.rotor_axis;
        float a = spin.y + spin.x * u_time;
        v_rotorAxis = k;
        v_rotorAngle = spin.x == 0.0 ? 0.0 : a;
        v_rotor = spin.x == 0.0 ? 0.0 : 1.0;
        if (spin.x != 0.0) {
          vec3 p = vsOutput.positionMC - vsInput.attributes.rotor_hub;
          float c = cos(a);
          float s = sin(a);
          vsOutput.positionMC = vsInput.attributes.rotor_hub + p * c + cross(k, p) * s + k * dot(k, p) * (1.0 - c);
        }
      }
    `,
    fragmentShaderText: `
      void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
        if (v_rotorAngle != 0.0) {
          vec3 k = normalize(mat3(czm_modelView) * v_rotorAxis);
          vec3 n = material.normalEC;
          float c = cos(v_rotorAngle);
          float s = sin(v_rotorAngle);
          material.normalEC = normalize(n * c + cross(k, n) * s + k * dot(k, n) * (1.0 - c));
        }${
          marked
            ? `
        if (u_markings > 0.5 && v_rotor > 0.5) {
          vec2 uv = fsInput.attributes.texCoord_0;
          material.diffuse = czm_srgbToLinear(texture(u_marked, vec2(uv.x, 1.0 - uv.y)).rgb);
        }`
            : ""
        }
      }
    `,
  });
  if (!marked) rotorShader = shader;
  return shader;
}
/** Per-layer symbology lifted from the project .lyrx (see backend lyrx_style.py). */
export interface LyrxStyle {
  geometry?: "polygon" | "line" | "point";
  fill?: string | null;
  fillOpacity?: number | null;
  outline?: string | null;
  outlineOpacity?: number | null;
  outlineWidth?: number | null;
  marker?: string | null;
  markerOpacity?: number | null;
  markerSize?: number | null;
  label?: string | null;
  hatch?: { color: string; opacity?: number; angle?: number; width?: number } | null;
}

export interface AssetRef {
  id: number;
  kind: string;
  dimension: string;
  web_path: string;
  placement_matrix?: number[] | null;
  lon?: number | null;
  lat?: number | null;
  height?: number | null;
  bbox?: { minx: number | null; miny: number | null; maxx: number | null; maxy: number | null };
  style?: LyrxStyle | null;
  radius?: number | null; // 3D model half-diagonal (metres), for accurate framing
  icon?: { web_path: string; point: boolean } | null; // lyrx legend icon / point glyph
  shader?: string | null; // viewer material treatment, e.g. "ground_dumps_pits"
  markings?: { web_path: string; label?: string } | null; // turbine fleet: marked rotor BaseMap
}

interface Loaded {
  asset: AssetRef;
  model?: Model;
  dataSource?: GeoJsonDataSource;
  imagery?: ImageryLayer;
  terrain?: CesiumTerrainProvider;
  visible: boolean;
  loading: boolean;
  queued?: boolean; // waiting in the load queue (wanted, not started)
  waiters?: Array<() => void>; // setVisible calls waiting for this entry's load to settle
  // Recency stamp (monotonic), bumped each time the layer is shown. Used by the VRAM
  // budget to evict the least-recently-used HIDDEN layer when we're over the cap.
  lastTouch?: number;
  // Authored 2D fill (alpha as the .lyrx specifies it) so the Transparency control can
  // scale it down from "match ArcGIS exactly" toward see-through.
  fillRgb?: Color;
  fillAlpha?: number;
  strokeRgb?: Color; // authored .lyrx outline colour, for restoring after Plan mode
  shader?: CustomShader; // the model's custom shader (turbine fleets: rotor spin + markings)
}

const COLOR_2D = Color.fromCssColorString("#3aa0ff");
const COLOR_3D = Color.fromCssColorString("#e0a060");

// "Here it is" pulse after flying to a layer. A big layer framed whole (a turbine fleet, a
// 40 km powerline) is a few pixels per piece, so the layer flashes: 3D models get a
// screen-space outline (silhouette, a fixed pixel width at ANY distance, so even a sub-pixel
// wire shows) plus a colour wash; 2D lines and polygon edges get a glowing overlay; 2D points
// get a pulsing dot. Cyan, so it can't be read as a Plan-view red/amber/green status.
const PULSE_COLOR = Color.fromCssColorString("#19e6ff");
const PULSE_BEATS = 3;
const PULSE_BEAT_MS = 800;
const PULSE_MAX_LINES = 4000; // overlay pieces per pulse; denser layers are sampled
const PULSE_MAX_POINTS = 2000;

/** Generated outline/hatch entities (not source features) - the pulse skips these, it
 *  traces the polygon rings directly instead. */
const derivedEntities = new WeakSet<Entity>();

interface Pulse {
  models: Loaded[];
  lines?: GroundPolylinePrimitive;
  lineMaterial?: Material;
  points?: CustomDataSource;
  env: number; // current envelope 0..1, read by the point callbacks
  raf: number;
}

// How many layers may hold GPU resources (textures + vertex buffers) at once. Hiding a
// layer keeps it "hot" so re-toggling is instant, but only up to this many; beyond it the
// least-recently-shown HIDDEN layer is destroyed and reloaded on demand. This is the dial
// between "keep everything resident" (high) and "destroy on hide" (low). Lower it hard on
// memory-constrained devices (phones/tablets) to keep dedicated VRAM bounded.
const DEFAULT_MEMORY_BUDGET = 24;

// Load queue. Showing a whole group (the 3D root of a project) used to start every download,
// parse and GPU upload at once - ACCIONA's 3D root is ~77 MB in 11 files - and the tab froze
// until all of them finished. Now layers queue and at most LOAD_PARALLEL load at a time,
// in-view and nearest first (re-ranked from the camera at each start, so panning steers what
// comes next), with a pause between loads so the page keeps drawing and taking input.
const LOAD_PARALLEL = 2;
const LOAD_GAP_MS = 16;

/** Generate hatch line segments (lon/lat) filling a polygon, mimicking an ArcGIS CIMHatchFill.
 *  Cesium can't pattern-fill terrain-draped polygons, so we draw the actual diagonal lines:
 *  rotate the polygon so the hatch is horizontal, run even-odd scanlines, rotate back. */
function hatchSegments(
  rings: Cartesian3[][],
  angleDeg: number,
): Array<[number, number][]> {
  // Polygon -> local metres about the centroid (equirectangular; fine at this scale).
  // Cartographic is in RADIANS; we work in degrees so the metres-per-degree factors apply.
  const carts = rings.map((r) => r.map((p) => Cartographic.fromCartesian(p)));
  let lon0 = 0, lat0 = 0, n = 0;
  for (const r of carts) for (const c of r) {
    lon0 += CMath.toDegrees(c.longitude); lat0 += CMath.toDegrees(c.latitude); n++;
  }
  lon0 /= n; lat0 /= n;
  const mPerLon = 111320 * Math.cos(CMath.toRadians(lat0)), mPerLat = 110540;
  const toXY = (c: Cartographic): [number, number] => [
    (CMath.toDegrees(c.longitude) - lon0) * mPerLon,
    (CMath.toDegrees(c.latitude) - lat0) * mPerLat,
  ];
  const th = CMath.toRadians(angleDeg), cs = Math.cos(-th), sn = Math.sin(-th);
  const rot = ([x, y]: [number, number]): [number, number] => [x * cs - y * sn, x * sn + y * cs];
  const polys = carts.map((r) => r.map((c) => rot(toXY(c)))); // rotated so hatch is horizontal

  let ymin = Infinity, ymax = -Infinity, xspan = 0, xmin = Infinity, xmax = -Infinity;
  for (const r of polys) for (const [x, y] of r) {
    if (y < ymin) ymin = y; if (y > ymax) ymax = y;
    if (x < xmin) xmin = x; if (x > xmax) xmax = x;
  }
  xspan = xmax - xmin;
  const ext = Math.max(ymax - ymin, xspan);
  if (!isFinite(ext) || ext <= 0) return [];
  const spacing = Math.min(Math.max(ext / 45, 12), 60); // sane density, any polygon size
  const ca = Math.cos(th), sa = Math.sin(th);
  const back = (x: number, y: number): [number, number] => {
    const ux = x * ca - y * sa, uy = x * sa + y * ca; // rotate back by +angle
    return [lon0 + ux / mPerLon, lat0 + uy / mPerLat];
  };

  const out: Array<[number, number][]> = [];
  for (let y = ymin + spacing * 0.5; y < ymax; y += spacing) {
    const xs: number[] = [];
    for (const r of polys) {
      for (let i = 0; i < r.length; i++) {
        const [x1, y1] = r[i], [x2, y2] = r[(i + 1) % r.length];
        if (y1 === y2) continue;
        if ((y1 <= y && y2 > y) || (y2 <= y && y1 > y)) {
          xs.push(x1 + ((y - y1) / (y2 - y1)) * (x2 - x1));
        }
      }
    }
    xs.sort((a, b) => a - b);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      out.push([back(xs[i], y), back(xs[i + 1], y)]);
    }
  }
  return out;
}

export class LayerManager {
  private viewer: Viewer;
  private loaded = new Map<number, Loaded>();
  private opacity = 1.0;
  private wireframe = false;
  /** Max layers allowed to hold GPU resources at once (LRU-evicted beyond this). */
  private memoryBudget = DEFAULT_MEMORY_BUDGET;
  /** Monotonic recency counter; the highest-stamped layers are kept, lowest evicted. */
  private touchClock = 0;
  /** Active Plan-mode colouring fn (status -> css). Kept so layers toggled on WHILE plan
   *  mode is active are coloured immediately on load, not left in their normal colours. */
  private planColorFn: ((assetId: number) => string | null) | null = null;
  /** Called when an asset begins/finishes loading (for the activity bar). */
  onLoadingStart: () => void = () => {};
  onLoadingEnd: () => void = () => {};

  constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /** Fleets with blade markings switched on ("Red tips"). Kept for fleets not loaded yet, so the
   *  state applies when they load. */
  private markings = new Set<number>();

  hasMarkings(assetId: number): boolean {
    return this.markings.has(assetId);
  }

  setMarkings(assetId: number, on: boolean): void {
    if (on) this.markings.add(assetId);
    else this.markings.delete(assetId);
    const e = this.loaded.get(assetId);
    if (e?.shader && e.asset.markings) {
      e.shader.setUniform("u_markings", on ? 1.0 : 0.0);
      this.viewer.scene.requestRender();
    }
  }

  async setVisible(asset: AssetRef, visible: boolean): Promise<void> {
    let entry = this.loaded.get(asset.id);
    if (!entry) {
      entry = { asset, visible, loading: false };
      this.loaded.set(asset.id, entry);
    }
    entry.visible = visible;
    if (visible) entry.lastTouch = ++this.touchClock; // most-recently-used: keep it hot
    const unloaded = !entry.model && !entry.dataSource && !entry.imagery && !entry.terrain;
    if (visible && unloaded && !entry.loading && !entry.queued) {
      entry.queued = true;
      this.queue.push(entry);
      this.onLoadingStart();
      this.pump();
    } else if (!visible && entry.queued) {
      // Switched off before its turn: drop it from the queue, nothing was fetched.
      this.queue.splice(this.queue.indexOf(entry), 1);
      entry.queued = false;
      this.settle(entry);
    }
    if (entry.queued || entry.loading) {
      await new Promise<void>((resolve) => (entry!.waiters ??= []).push(resolve));
    }
    this.applyVisibility(entry);
  }

  private queue: Loaded[] = [];
  private active = 0;

  /** Start queued loads up to LOAD_PARALLEL, best-ranked first. */
  private pump(): void {
    while (this.active < LOAD_PARALLEL && this.queue.length) {
      let best = 0;
      let bestScore = Infinity;
      for (let i = 0; i < this.queue.length; i++) {
        const s = this.loadPriority(this.queue[i].asset);
        if (s < bestScore) {
          bestScore = s;
          best = i;
        }
      }
      const entry = this.queue.splice(best, 1)[0];
      entry.queued = false;
      this.active++;
      void this.load(entry)
        .catch((err) => console.error(`[layers] load failed: ${entry.asset.web_path}`, err))
        .finally(() => {
          this.settle(entry);
          // Project switched (clearAll) while this was loading: don't leave it in the new scene.
          if (this.loaded.get(entry.asset.id) !== entry) {
            if (entry.terrain && this.viewer.terrainProvider === entry.terrain) {
              this.viewer.terrainProvider = new EllipsoidTerrainProvider();
            }
            this.unload(entry);
          } else this.applyVisibility(entry);
          this.enforceMemoryBudget();
          this.viewer.scene.requestRender();
          setTimeout(() => {
            this.active--;
            this.pump();
          }, LOAD_GAP_MS);
        });
    }
  }

  /** Lower loads sooner: anything in the camera's view before anything outside it, then by
   *  distance from the camera to the layer's nearest edge. */
  private loadPriority(asset: AssetRef): number {
    const sphere = this.sphereFor(asset);
    if (!sphere) return 2e9;
    const camera = this.viewer.camera;
    const cull = camera.frustum.computeCullingVolume(camera.positionWC, camera.directionWC, camera.upWC);
    const inView = cull.computeVisibility(sphere) !== Intersect.OUTSIDE;
    const gap = Math.max(Cartesian3.distance(camera.positionWC, sphere.center) - sphere.radius, 0);
    return (inView ? 0 : 1e9) + gap;
  }

  /** A queued entry's load finished or was dropped: release its waiters and the activity count. */
  private settle(entry: Loaded): void {
    const waiters = entry.waiters ?? [];
    entry.waiters = undefined;
    waiters.forEach((resolve) => resolve());
    this.onLoadingEnd();
  }

  /** Show/hide whatever the entry has loaded to match its CURRENT wanted state (it may have been
   *  switched off while it was loading). */
  private applyVisibility(entry: Loaded): void {
    const { visible, asset } = entry;
    if (entry.model) entry.model.show = visible;
    if (entry.dataSource) entry.dataSource.show = visible;
    if (entry.imagery) entry.imagery.show = visible;
    if (entry.terrain) {
      // Cesium has ONE active terrain provider: toggling a terrain asset on swaps it
      // in; toggling off returns to the flat ellipsoid.
      if (visible) {
        this.viewer.terrainProvider = entry.terrain;
        this.activeTerrainId = asset.id;
      } else if (this.activeTerrainId === asset.id) {
        this.viewer.terrainProvider = new EllipsoidTerrainProvider();
        this.activeTerrainId = null;
      }
    }
    this.enforceMemoryBudget();
    this.viewer.scene.requestRender();
  }

  /** Set how many layers may hold GPU resources at once (LRU eviction beyond this). Lower
   *  it on memory-constrained devices to keep dedicated VRAM bounded; 0 = destroy on hide. */
  setMemoryBudget(maxLoaded: number): void {
    this.memoryBudget = Math.max(0, Math.floor(maxLoaded));
    this.enforceMemoryBudget();
  }

  /** Destroy the GPU resources (textures, buffers) of the least-recently-shown HIDDEN
   *  layers until the number holding GPU memory is back within budget. Visible layers are
   *  never evicted (you can't free what's on screen); terrain is shared, not counted here.
   *  An evicted layer drops back to "unloaded" and transparently reloads when next shown. */
  private enforceMemoryBudget(): void {
    const live = [...this.loaded.values()].filter(
      (e) => e.model || e.dataSource || e.imagery,
    );
    if (live.length <= this.memoryBudget) return;
    const evictable = live
      .filter((e) => !e.visible && !e.loading)
      .sort((a, b) => (a.lastTouch ?? 0) - (b.lastTouch ?? 0)); // oldest-shown first
    let over = live.length - this.memoryBudget;
    for (const entry of evictable) {
      if (over <= 0) break;
      this.unload(entry);
      this.loaded.delete(entry.asset.id);
      over--;
    }
  }

  /** Remove + destroy one entry's GPU-backed resources (mirrors clearAll, per-entry). */
  private unload(entry: Loaded): void {
    if (entry.model) this.viewer.scene.primitives.remove(entry.model); // remove() destroys
    if (entry.dataSource) this.viewer.dataSources.remove(entry.dataSource, true);
    if (entry.imagery) this.viewer.imageryLayers.remove(entry.imagery, true);
    // A marked fleet's shader is its own (it holds the marked texture); the shared ones stay.
    if (entry.shader && entry.asset.markings) entry.shader.destroy();
    entry.shader = undefined;
    entry.model = undefined;
    entry.dataSource = undefined;
    entry.imagery = undefined;
  }

  /** Resolve a Cesium pick result (scene.pick) back to the asset id it belongs to. */
  assetIdForPick(picked: unknown): number | null {
    if (!picked) return null;
    const p = picked as { primitive?: unknown; id?: { entityCollection?: { owner?: unknown } } };
    for (const [id, entry] of this.loaded) {
      if (entry.model && p.primitive === entry.model) return id;
      if (entry.dataSource && p.id?.entityCollection?.owner === entry.dataSource) return id;
    }
    return null;
  }

  private activeTerrainId: number | null = null;

  isVisible(assetId: number): boolean {
    return this.loaded.get(assetId)?.visible ?? false;
  }

  private async load(entry: Loaded): Promise<void> {
    entry.loading = true;
    const { asset } = entry;
    try {
      if (asset.kind === "gltf" && asset.placement_matrix) {
        const shader =
          asset.shader === "ground_dumps_pits"
            ? groundDumpsPitsShader()
            : asset.shader === "turbine_rotor"
              ? turbineRotorShader(asset.markings?.web_path)
              : undefined;
        if (shader && asset.markings) shader.setUniform("u_markings", this.markings.has(asset.id) ? 1.0 : 0.0);
        const model = await Model.fromGltfAsync({
          url: asset.web_path,
          modelMatrix: Matrix4.fromArray(asset.placement_matrix),
          // White HIGHLIGHT (multiply by white = no tint) lets the GLB's own materials and
          // textures show through; the alpha still drives the Transparency control. Plan/RAG
          // mode swaps this for a solid status colour (REPLACE) via setPlanColoring.
          color: Color.WHITE.withAlpha(this.opacity),
          colorBlendMode: ColorBlendMode.HIGHLIGHT,
          // GLB verts are authored local-ENU (X=east, Y=north, Z=up). Tell Cesium the
          // model is already Z-up/X-forward so it does NOT apply its default Y-up->Z-up
          // tip (which made the surface render on its side).
          upAxis: Axis.Z,
          forwardAxis: Axis.X,
          enableDebugWireframe: true, // required for the wireframe toggle (WebGL1)
          ...(shader ? { customShader: shader } : {}),
        });
        entry.shader = shader;
        model.debugWireframe = this.wireframe;
        this.viewer.scene.primitives.add(model);
        entry.model = model;
        // fromGltfAsync returns once the file is fetched; buffers and textures then go to the GPU
        // over the next frames. Hold this queue slot until that's done too (capped, so a model
        // that never readies can't stall the queue).
        if (!model.ready) {
          this.viewer.scene.requestRender();
          await new Promise<void>((resolve) => {
            const done = () => {
              offReady();
              offError();
              clearTimeout(timer);
              resolve();
            };
            const offReady = model.readyEvent.addEventListener(done);
            const offError = model.errorEvent.addEventListener(done);
            const timer = setTimeout(done, 30000);
          });
        }
      } else if (asset.kind === "geojson") {
        const is2d = asset.dimension === "2D";
        const st = asset.style || null;
        const base = is2d ? COLOR_2D : COLOR_3D;
        // Defaults come from the .lyrx symbology when present, else the house 2D/3D colours.
        const stroke = st?.outline
          ? Color.fromCssColorString(st.outline).withAlpha(st.outlineOpacity ?? 1)
          : base;
        // Match the ArcGIS map: use the .lyrx fill alpha verbatim. The Transparency control
        // then scales it down (this.opacity) when the reviewer wants to see through it.
        // A STYLED layer with no fill (outline-only, e.g. WN Development Envelope / Native
        // Title boundaries) must be transparent inside - NOT the default blue. Only a fully
        // UNSTYLED 2D layer gets the faint house-blue fill.
        const fillRgb = st?.fill ? Color.fromCssColorString(st.fill) : base;
        const fillAlpha = st?.fill ? (st.fillOpacity ?? 1) : st ? 0 : 0.25;
        entry.fillRgb = fillRgb;
        entry.fillAlpha = fillAlpha;
        entry.strokeRgb = stroke;
        const fill = fillRgb.withAlpha(fillAlpha * this.opacity);
        const strokeWidth = st?.outlineWidth ? Math.max(st.outlineWidth, 1) : 2;
        const ds = await GeoJsonDataSource.load(asset.web_path, {
          clampToGround: true,
          stroke,
          strokeWidth,
          fill,
          markerColor: st?.marker ? Color.fromCssColorString(st.marker) : base,
        });
        // Drape everything onto the terrain surface like ArcGIS 2D data: fills paint onto
        // the ground via terrain classification (conform to every undulation), and polygon
        // outlines become ground-clamped boundary lines (a polygon's own outline floats at
        // height 0 and clips through hills, which is what looked wrong).
        const t = this.viewer.clock.currentTime;
        const outlines: Entity[] = [];
        for (const e of ds.entities.values) {
          if (e.polygon) {
            e.polygon.perPositionHeight = false as never;
            e.polygon.height = undefined;
            e.polygon.classificationType = ClassificationType.BOTH as never;
            // A hatched layer has no solid infill - the diagonal lines ARE the fill.
            e.polygon.material = (st?.hatch ? Color.TRANSPARENT : fill) as never;
            e.polygon.outline = false as never;
            const h = e.polygon.hierarchy?.getValue(t);
            const rings: Cartesian3[][] = h
              ? [h.positions, ...((h.holes ?? []).map((x: { positions: Cartesian3[] }) => x.positions))]
              : [];
            if (st?.outline) {
              for (const ring of rings) {
                if (!ring || ring.length < 2) continue;
                outlines.push(
                  new Entity({
                    polyline: { positions: [...ring, ring[0]], width: strokeWidth, material: stroke, clampToGround: true },
                  }),
                );
              }
            }
            // Real hatching: diagonal clamped polylines clipped to the polygon.
            if (st?.hatch && rings.length) {
              const hColor = Color.fromCssColorString(st.hatch.color).withAlpha(st.hatch.opacity ?? 1);
              const hw = Math.max(st.hatch.width ?? 1, 1);
              for (const [a, b] of hatchSegments(rings, st.hatch.angle ?? 45)) {
                outlines.push(
                  new Entity({
                    polyline: {
                      positions: [Cartesian3.fromDegrees(a[0], a[1]), Cartesian3.fromDegrees(b[0], b[1])],
                      width: hw,
                      material: hColor,
                      clampToGround: true,
                    },
                  }),
                );
              }
            }
          }
          if (e.polyline) {
            e.polyline.clampToGround = true as never;
            e.polyline.material = stroke as never;
            e.polyline.width = strokeWidth as never;
          }
          // Point layers (Homesteads, Mines): show the real ESRI glyph icon, not a pin.
          if (e.billboard && asset.icon?.point) {
            const px = Math.round((st?.markerSize ?? 10) * 2.2);
            e.billboard.image = asset.icon.web_path as never;
            e.billboard.width = px as never;
            e.billboard.height = px as never;
            e.billboard.color = Color.WHITE as never; // no tint
            e.billboard.verticalOrigin = VerticalOrigin.CENTER as never;
            e.billboard.heightReference = HeightReference.CLAMP_TO_GROUND as never;
            e.billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY as never;
          }
        }
        for (const o of outlines) {
          derivedEntities.add(o);
          ds.entities.add(o);
        }
        await this.viewer.dataSources.add(ds);
        entry.dataSource = ds;
      } else if (asset.kind === "raster_tiles") {
        const b = asset.bbox;
        const rectangle =
          b && b.minx != null
            ? Rectangle.fromDegrees(b.minx!, b.miny!, b.maxx!, b.maxy!)
            : undefined;
        const provider = new UrlTemplateImageryProvider({
          url: asset.web_path, // /api/tiles/{id}/{z}/{x}/{y}.png
          rectangle,
          maximumLevel: 19,
        });
        const layer = ImageryLayer.fromProviderAsync(Promise.resolve(provider), { rectangle });
        this.viewer.imageryLayers.add(layer);
        entry.imagery = layer;
      } else if (asset.kind === "terrain") {
        entry.terrain = await CesiumTerrainProvider.fromUrl(asset.web_path);
        this.viewer.terrainProvider = entry.terrain;
        this.activeTerrainId = asset.id;
      }
      // If Plan mode is active, colour this freshly-loaded layer right away (RAG by status),
      // so toggling layers on in plan mode applies the colours immediately.
      if (this.planColorFn) this.applyPlanColor(entry);
    } finally {
      entry.loading = false;
    }
  }

  setOpacity(value: number): void {
    this.opacity = value;
    for (const entry of this.loaded.values()) {
      if (entry.model) {
        // Keep the model's current tint (white for textured, or a plan colour); change alpha.
        const c = entry.model.color;
        entry.model.color = new Color(c.red, c.green, c.blue, value);
      }
      if (entry.dataSource && entry.fillRgb && entry.fillAlpha != null) {
        const mat = entry.fillRgb.withAlpha(entry.fillAlpha * value);
        for (const e of entry.dataSource.entities.values) {
          if (e.polygon) e.polygon.material = mat as never;
        }
      }
    }
    this.viewer.scene.requestRender();
  }

  setWireframe(on: boolean): void {
    this.wireframe = on;
    for (const entry of this.loaded.values()) {
      if (entry.model) entry.model.debugWireframe = on;
    }
    this.viewer.scene.requestRender();
  }

  setDimensionFilter(dim: "2D" | "3D" | "all"): void {
    for (const entry of this.loaded.values()) {
      if (entry.terrain) continue; // terrain is the ground - never filtered out
      const match = dim === "all" || entry.asset.dimension === dim;
      const show = entry.visible && match;
      if (entry.model) entry.model.show = show;
      if (entry.dataSource) entry.dataSource.show = show;
      if (entry.imagery) entry.imagery.show = show;
    }
    this.viewer.scene.requestRender();
  }

  /** A world-space bounding sphere for an asset, accurate to its real geometry when loaded
   *  (model/data source), else derived from its lon/lat bbox, else a small sphere at its
   *  centroid. This is what makes "fly to" frame the whole object instead of a fixed zoom. */
  private sphereFor(asset: AssetRef): BoundingSphere | null {
    const e = this.loaded.get(asset.id);
    if (e?.model && e.model.ready) {
      return BoundingSphere.clone(e.model.boundingSphere);
    }
    const b = asset.bbox;
    if (b && b.minx != null && b.miny != null && b.maxx != null && b.maxy != null) {
      const pts = [
        Cartesian3.fromDegrees(b.minx, b.miny),
        Cartesian3.fromDegrees(b.maxx, b.miny),
        Cartesian3.fromDegrees(b.maxx, b.maxy),
        Cartesian3.fromDegrees(b.minx, b.maxy),
      ];
      const s = BoundingSphere.fromPoints(pts);
      s.radius = Math.max(s.radius, 30); // tiny footprints still get a sane frame
      return s;
    }
    if (asset.lon != null && asset.lat != null) {
      // 3D models carry a real half-diagonal radius (metres), so we can frame them
      // accurately without loading the geometry; fall back to a small default otherwise.
      return new BoundingSphere(
        Cartesian3.fromDegrees(asset.lon, asset.lat, asset.height ?? 0),
        asset.radius && asset.radius > 0 ? asset.radius : 200,
      );
    }
    return null;
  }

  /** Fly so the asset fills a comfortable portion of the view (works for 2D and 3D). */
  flyToAsset(asset: AssetRef): void {
    const sphere = this.sphereFor(asset);
    if (!sphere) return;
    sphere.radius *= 1.25; // a little breathing room around the object
    this.flyAndPulse(sphere, [asset.id]);
  }

  /** Fly to a sphere, then pulse the layer's visible pieces so they're easy to spot. The
   *  overlay is built as the flight starts (ground lines build in a worker) and the pulse
   *  runs when it lands - or when the user takes over the camera mid-flight. */
  private flightSeq = 0;
  private flyAndPulse(sphere: BoundingSphere, assetIds: number[]): void {
    const seq = ++this.flightSeq;
    this.stopPulse();
    const pulse = this.preparePulse(assetIds);
    const land = () => {
      if (seq === this.flightSeq && pulse) this.runPulse(pulse);
    };
    this.viewer.camera.flyToBoundingSphere(sphere, { duration: 1.2, complete: land, cancel: land });
  }

  private pulse: Pulse | null = null;

  private preparePulse(assetIds: number[]): Pulse | null {
    const t = this.viewer.clock.currentTime;
    const models: Loaded[] = [];
    const rings: Cartesian3[][] = [];
    const dots: Cartesian3[] = [];
    for (const id of assetIds) {
      const e = this.loaded.get(id);
      if (!e || !e.visible) continue;
      if (e.model && e.model.show && !e.model.isDestroyed()) models.push(e);
      if (e.dataSource && e.dataSource.show) {
        for (const ent of e.dataSource.entities.values) {
          if (derivedEntities.has(ent)) continue;
          if (ent.polyline) {
            const p = ent.polyline.positions?.getValue(t) as Cartesian3[] | undefined;
            if (p && p.length > 1) rings.push(p);
          }
          if (ent.polygon) {
            const h = ent.polygon.hierarchy?.getValue(t);
            if (h) {
              for (const r of [h.positions, ...(h.holes ?? []).map((x: { positions: Cartesian3[] }) => x.positions)]) {
                if (r && r.length > 2) rings.push([...r, r[0]]);
              }
            }
          }
          if ((ent.billboard || ent.point) && ent.position) {
            const p = ent.position.getValue(t);
            if (p) dots.push(p);
          }
        }
      }
    }
    if (!models.length && !rings.length && !dots.length) return null;

    const pulse: Pulse = { models, env: 0, raf: 0 };
    const scene = this.viewer.scene;
    if (rings.length && GroundPolylinePrimitive.isSupported(scene)) {
      const stride = Math.ceil(rings.length / PULSE_MAX_LINES);
      const material = Material.fromType("PolylineGlow", {
        color: PULSE_COLOR.withAlpha(0),
        glowPower: 0.25,
        taperPower: 1.0,
      });
      pulse.lineMaterial = material;
      pulse.lines = scene.groundPrimitives.add(
        new GroundPolylinePrimitive({
          geometryInstances: rings
            .filter((_, i) => i % stride === 0)
            .map((positions) => new GeometryInstance({ geometry: new GroundPolylineGeometry({ positions, width: 16 }) })),
          appearance: new PolylineMaterialAppearance({ material, translucent: true }),
          classificationType: ClassificationType.BOTH,
        }),
      ) as GroundPolylinePrimitive;
    }
    if (dots.length) {
      const stride = Math.ceil(dots.length / PULSE_MAX_POINTS);
      const ds = new CustomDataSource("pulse");
      const size = new CallbackProperty(() => 10 + 20 * pulse.env, false);
      const color = new CallbackProperty(() => PULSE_COLOR.withAlpha(pulse.env), false);
      const outline = new CallbackProperty(() => Color.WHITE.withAlpha(pulse.env), false);
      dots.forEach((position, i) => {
        if (i % stride) return;
        ds.entities.add({
          position,
          point: {
            pixelSize: size as never,
            color: color as never,
            outlineColor: outline as never,
            outlineWidth: 2,
            heightReference: HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
      });
      void this.viewer.dataSources.add(ds);
      pulse.points = ds;
    }
    this.pulse = pulse;
    return pulse;
  }

  private runPulse(pulse: Pulse): void {
    if (this.pulse !== pulse) return;
    const scene = this.viewer.scene;
    const tint = !this.planColorFn; // Plan view's status colours stay put; outline only
    const start = performance.now();
    const total = PULSE_BEATS * PULSE_BEAT_MS;
    const frame = (now: number) => {
      if (this.pulse !== pulse) return;
      const t = now - start;
      if (t >= total) {
        this.stopPulse();
        return;
      }
      // Three soft beats, each a little weaker: sin^2 rises and falls within a beat.
      const beat = Math.sin((Math.PI * (t % PULSE_BEAT_MS)) / PULSE_BEAT_MS);
      pulse.env = beat * beat * (1 - (0.25 * t) / total);
      for (const e of pulse.models) {
        const m = e.model;
        if (!m || m.isDestroyed()) continue;
        // Needs a stencil buffer (Cesium asks for one by default); without it this is a no-op.
        m.silhouetteColor = PULSE_COLOR.withAlpha(pulse.env);
        m.silhouetteSize = 1 + 4 * pulse.env;
        if (tint) {
          m.color = PULSE_COLOR.withAlpha(this.opacity);
          m.colorBlendMode = ColorBlendMode.MIX;
          m.colorBlendAmount = 0.7 * pulse.env;
        }
      }
      if (pulse.lineMaterial) pulse.lineMaterial.uniforms.color = PULSE_COLOR.withAlpha(pulse.env);
      scene.requestRender();
      pulse.raf = requestAnimationFrame(frame);
    };
    pulse.raf = requestAnimationFrame(frame);
  }

  /** End the current pulse (if any) and put every touched layer back exactly as it was. */
  private stopPulse(): void {
    const pulse = this.pulse;
    if (!pulse) return;
    this.pulse = null;
    cancelAnimationFrame(pulse.raf);
    for (const e of pulse.models) {
      const m = e.model;
      if (!m || m.isDestroyed()) continue;
      m.silhouetteSize = 0;
      this.applyModelLook(e);
    }
    if (pulse.lines) this.viewer.scene.groundPrimitives.remove(pulse.lines); // remove() destroys
    if (pulse.points) void this.viewer.dataSources.remove(pulse.points, true);
    this.viewer.scene.requestRender();
  }

  /** Per-axis median of a set of points - a centre robust to far-flung outliers. */
  private medianCenter(pts: Cartesian3[]): Cartesian3 {
    const med = (vals: number[]): number => {
      const s = [...vals].sort((a, b) => a - b);
      return s[Math.floor(s.length / 2)];
    };
    return new Cartesian3(med(pts.map((p) => p.x)), med(pts.map((p) => p.y)), med(pts.map((p) => p.z)));
  }

  /** Frame an item's assets. Compact layers frame whole; but a very large/spread-out 3D layer
   *  (39 km haul road, the powerline, trucks scattered down a route) would frame to a useless
   *  overview - so for those we zoom into the DENSE core ("where most of the pieces are"),
   *  capped to a scale where you can actually see the models and pan/follow along from there. */
  flyToAssets(assets: AssetRef[]): void {
    const refs = assets
      .map((a) => ({ a, s: this.sphereFor(a) }))
      .filter((x): x is { a: AssetRef; s: BoundingSphere } => !!x.s);
    if (refs.length === 0) return;
    const spheres = refs.map((x) => x.s);
    let target = spheres.reduce((acc, s) => BoundingSphere.union(acc, s, acc), BoundingSphere.clone(spheres[0]));

    const is3D = refs.some((x) => x.a.dimension === "3D");
    if (is3D && target.radius > 3000) {
      const centers = spheres.map((s) => s.center);
      const med = this.medianCenter(centers);
      const dists = centers.map((c) => Cartesian3.distance(c, med)).sort((a, b) => a - b);
      const p75 = dists[Math.min(dists.length - 1, Math.floor(dists.length * 0.75))]; // bulk, not outliers
      const maxPart = Math.max(...spheres.map((s) => s.radius));
      const coreR = Math.min(Math.max(p75 + maxPart, 250), 3000); // cap so it zooms IN
      target = new BoundingSphere(med, coreR);
    }
    target.radius *= 1.25;
    this.flyAndPulse(target, refs.map((x) => x.a.id));
  }

  flyToVisible(): void {
    // Fly to the first visible asset with a known centroid.
    for (const entry of this.loaded.values()) {
      if (entry.visible && entry.asset.lon != null && entry.asset.lat != null) {
        this.viewer.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            entry.asset.lon,
            entry.asset.lat,
            (entry.asset.height ?? 0) + 4000,
          ),
        });
        return;
      }
    }
  }

  /** True orthographic top-down for alignment checks (no perspective distortion).
   *  Toggling off restores the normal perspective camera. */
  topDown(on: boolean): void {
    const camera = this.viewer.camera;
    if (on) {
      const center = camera.positionCartographic;
      camera.setView({
        destination: Cartesian3.fromRadians(
          center.longitude,
          center.latitude,
          Math.max(center.height, 2000),
        ),
        orientation: { heading: 0, pitch: -Math.PI / 2, roll: 0 },
      });
      camera.switchToOrthographicFrustum();
    } else {
      camera.switchToPerspectiveFrustum();
    }
    this.viewer.scene.requestRender();
  }

  /** Plan mode: recolour every loaded layer by a status colour (RAG sign-off map).
   *  Pass null to restore the normal 2D-blue / 3D-orange scheme. The fn is remembered so
   *  layers toggled on while plan mode is active are coloured the moment they load. */
  setPlanColoring(colorFor: ((assetId: number) => string | null) | null): void {
    this.planColorFn = colorFor;
    for (const entry of this.loaded.values()) this.applyPlanColor(entry);
    this.viewer.scene.requestRender();
  }

  /** Colour one loaded entry per the active Plan-mode fn (or restore its normal look). */
  private applyPlanColor(entry: Loaded): void {
    const css = this.planColorFn ? this.planColorFn(entry.asset.id) : null;
    const plan = css ? Color.fromCssColorString(css) : null;
    this.applyModelLook(entry);
    if (entry.dataSource) {
      const fillRgb = entry.fillRgb ?? COLOR_2D;
      const fillAlpha = entry.fillAlpha ?? 0.25;
      const stroke = entry.strokeRgb ?? COLOR_2D;
      for (const e of entry.dataSource.entities.values) {
        if (e.polygon) {
          e.polygon.material = (plan ? plan.withAlpha(0.45) : fillRgb.withAlpha(fillAlpha * this.opacity)) as never;
        }
        if (e.polyline) e.polyline.material = (plan ?? stroke) as never;
        if (e.point) e.point.color = (plan ?? stroke) as never;
      }
    }
  }

  /** A model's normal tint: its Plan-view status colour, else white (textures untouched). */
  private applyModelLook(entry: Loaded): void {
    if (!entry.model) return;
    const css = this.planColorFn ? this.planColorFn(entry.asset.id) : null;
    if (css) {
      entry.model.color = Color.fromCssColorString(css).withAlpha(this.opacity); // solid RAG colour over the texture
      entry.model.colorBlendMode = ColorBlendMode.REPLACE;
    } else {
      entry.model.color = Color.WHITE.withAlpha(this.opacity); // restore textures
      entry.model.colorBlendMode = ColorBlendMode.HIGHLIGHT;
    }
  }

  /** Remove every loaded layer from the scene (project switch). */
  clearAll(): void {
    this.stopPulse();
    for (const entry of this.queue.splice(0)) {
      entry.queued = false;
      this.settle(entry);
    }
    for (const entry of this.loaded.values()) {
      if (entry.model) this.viewer.scene.primitives.remove(entry.model);
      if (entry.dataSource) this.viewer.dataSources.remove(entry.dataSource, true);
      if (entry.imagery) this.viewer.imageryLayers.remove(entry.imagery, true);
      if (entry.shader && entry.asset.markings) entry.shader.destroy();
    }
    if (this.activeTerrainId !== null) {
      this.viewer.terrainProvider = new EllipsoidTerrainProvider();
      this.activeTerrainId = null;
    }
    this.loaded.clear();
    this.viewer.scene.requestRender();
  }
}
